import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Zone, RadiusConfig, PostcodeConfig, PolygonConfig, ZoneType } from '../types';
import { ApiZone } from '../types/api';
import * as zoneApi from '../services/zoneApiService';
import { logger } from '../lib/logger';

const log = logger.createNamespace('useZones');

interface UseZonesResult {
  zones: Zone[];
  loading: boolean;
  error: string | null;
  addZone: (
    name: string,
    type: ZoneType,
    config: RadiusConfig | PostcodeConfig | PolygonConfig
  ) => Promise<{ error: Error | null }>;
  updateZone: (zoneId: string, updates: Partial<Zone>) => Promise<{ error: Error | null }>;
  deleteZone: (zoneId: string) => Promise<{ error: Error | null }>;
  toggleZone: (zoneId: string) => Promise<{ error: Error | null }>;
  refetch: () => Promise<void>;
}

const transformApiZone = (apiZone: ApiZone): Zone => ({
  id: apiZone.id,
  name: apiZone.name,
  type: apiZone.type,
  enabled: apiZone.enabled,
  config: apiZone.config as unknown as RadiusConfig | PostcodeConfig | PolygonConfig,
  createdAt: new Date(apiZone.created_at),
});

export const useZones = (): UseZonesResult => {
  const { user } = useAuth();
  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchZones = useCallback(async () => {
    log.debug('Fetching zones', { userId: user?.id });

    if (!user) {
      log.debug('No user, clearing zones');
      setZones([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await zoneApi.getZones();
      const transformedZones = (data || []).map(transformApiZone);
      log.debug('Zones loaded', { count: transformedZones.length });
      setZones(transformedZones);
    } catch (err) {
      log.error('Failed to fetch zones', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch zones');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  const addZone = useCallback(async (
    name: string,
    type: ZoneType,
    config: RadiusConfig | PostcodeConfig | PolygonConfig
  ) => {
    if (!user) {
      return { error: new Error('Not authenticated') };
    }

    log.info('Creating zone', { name, type });

    try {
      const data = await zoneApi.createZone({
        name,
        type,
        config: config as unknown as Record<string, unknown>,
        enabled: true,
      });

      log.info('Zone created successfully', { zoneId: data.id });
      setZones(prev => [transformApiZone(data), ...prev]);
      return { error: null };
    } catch (err) {
      log.error('Failed to create zone', err);
      return { error: err as Error };
    }
  }, [user]);

  const updateZone = useCallback(async (zoneId: string, updates: Partial<Zone>) => {
    if (!user) {
      return { error: new Error('Not authenticated') };
    }

    log.debug('Updating zone', { zoneId, updates });

    try {
      const apiUpdates: Record<string, unknown> = {};
      if (updates.name !== undefined) apiUpdates.name = updates.name;
      if (updates.enabled !== undefined) apiUpdates.enabled = updates.enabled;
      if (updates.config !== undefined) apiUpdates.config = updates.config;

      await zoneApi.updateZone(zoneId, apiUpdates);
      log.debug('Zone updated successfully');

      setZones(prev => prev.map(zone =>
        zone.id === zoneId ? { ...zone, ...updates } : zone
      ));
      return { error: null };
    } catch (err) {
      log.error('Failed to update zone', err);
      return { error: err as Error };
    }
  }, [user]);

  const deleteZone = useCallback(async (zoneId: string) => {
    if (!user) {
      return { error: new Error('Not authenticated') };
    }

    log.info('Deleting zone', { zoneId });

    try {
      await zoneApi.deleteZone(zoneId);
      log.info('Zone deleted successfully');
      setZones(prev => prev.filter(zone => zone.id !== zoneId));
      return { error: null };
    } catch (err) {
      log.error('Failed to delete zone', err);
      return { error: err as Error };
    }
  }, [user]);

  const toggleZone = useCallback(async (zoneId: string) => {
    const zone = zones.find(z => z.id === zoneId);
    if (!zone) {
      return { error: new Error('Zone not found') };
    }
    return updateZone(zoneId, { enabled: !zone.enabled });
  }, [zones, updateZone]);

  return {
    zones,
    loading,
    error,
    addZone,
    updateZone,
    deleteZone,
    toggleZone,
    refetch: fetchZones,
  };
};
